import React, { useCallback } from "react";
import { Link } from "react-router-dom";
import { Star, ChevronLeft, ChevronRight } from "lucide-react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { TESTIMONIALS, IMAGES } from "../data/site";
import { SectionHeading } from "../components/SectionHeading";
import { Seo } from "../components/Seo";
import { SEO, aggregateRatingSchema, breadcrumbSchema } from "../data/seo";
import { VideoTestimonialsSection } from "../components/VideoTestimonialsSection";

const TestimonialsCarousel = () => {
  const autoplay = React.useRef(
    Autoplay({ delay: 3500, stopOnInteraction: false }),
  );
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true, align: "start", slidesToScroll: 1 },
    [autoplay.current],
  );
  const [selectedIndex, setSelectedIndex] = React.useState(0);

  React.useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    onSelect();
    return () => emblaApi.off("select", onSelect);
  }, [emblaApi]);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  const slides = TESTIMONIALS.concat(TESTIMONIALS);
  const total = TESTIMONIALS.length;

  return (
    <div className="relative">
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex gap-6">
          {slides.map((t, i) => (
            <div
              key={i}
              className="flex-none w-full md:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)] bg-white p-8 md:p-10 border border-ink/5"
              data-testid={`review-card-${i}`}
            >
              <div className="flex gap-1 mb-5">
                {[...Array(Math.floor(t.rating))].map((_, j) => (
                  <Star
                    key={`full-${j}`}
                    size={14}
                    className="fill-gold text-gold"
                    strokeWidth={0}
                  />
                ))}
                {t.rating % 1 !== 0 && (
                  <div key="half" className="relative w-3.5 h-3.5">
                    <Star size={14} className="text-gold" strokeWidth={0} />
                    <div className="absolute inset-0 overflow-hidden w-1/2">
                      <Star
                        size={14}
                        className="fill-gold text-gold"
                        strokeWidth={0}
                      />
                    </div>
                  </div>
                )}
              </div>
              <p className="font-serif text-xl md:text-2xl font-light leading-snug text-ink">
                "{t.text}"
              </p>
              <div className="mt-6 pt-6 border-t border-ink/10">
                <div className="text-sm tracking-widest uppercase text-ink">
                  {t.name}
                </div>
                <div className="text-xs tracking-widest uppercase text-ink/50 mt-1">
                  {t.location}
                </div>
                {t.date && (
                  <div className="text-xs tracking-widest uppercase text-ink/40 mt-2">
                    {t.date}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="flex items-center justify-center gap-4 mt-10">
        <button
          onClick={scrollPrev}
          className="w-11 h-11 border border-ink/20 flex items-center justify-center hover:border-gold hover:text-gold transition-colors"
          aria-label="Previous"
        >
          <ChevronLeft size={18} strokeWidth={1.5} />
        </button>
        <span className="text-[10px] tracking-[0.35em] uppercase text-black/50 font-mono">
          drag or click
        </span>

        <button
          onClick={scrollNext}
          className="w-11 h-11 border border-ink/20 flex items-center justify-center hover:border-gold hover:text-gold transition-colors"
          aria-label="Next"
        >
          <ChevronRight size={18} strokeWidth={1.5} />
        </button>
      </div>

      {/* Count indicator */}
      <div className="flex items-center justify-center gap-3 mt-5">
        {TESTIMONIALS.map((_, i) => (
          <button
            key={i}
            onClick={() => emblaApi?.scrollTo(i)}
            className={`transition-all duration-300 rounded-full ${
              i === selectedIndex % total
                ? "w-6 h-1.5 bg-gold"
                : "w-1.5 h-1.5 bg-ink/20 hover:bg-ink/40"
            }`}
            aria-label={`Go to review ${i + 1}`}
          />
        ))}
        <span className="ml-3 text-[10px] tracking-[0.3em] uppercase text-ink/40 font-mono">
          {(selectedIndex % total) + 1} <span className="text-ink/20">/</span>{" "}
          {total} <span className="text-ink/20"></span>
        </span>
      </div>
    </div>
  );
};

export const ReviewsPage = () => {
  return (
    <div data-testid="reviews-page">
      <Seo
        title={SEO.reviews.title}
        description={SEO.reviews.description}
        schemas={[
          aggregateRatingSchema(),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Reviews", path: "/reviews" },
          ]),
        ]}
      />
      <section className="relative h-[45vh] min-h-[340px] overflow-hidden">
        <img
          src="https://galleria-dental-media.s3.eu-north-1.amazonaws.com/uploads/gallery/fc9102a3-c44c-405e-a682-9fd857bd9a84.png"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          style={{ objectPosition: "10% 50%" }}
        />
        <div className="absolute inset-0 bg-ink/60" />
        <div className="relative h-full container-wide flex flex-col justify-end pb-16 text-white">
          <div className="label-eyebrow text-gold mb-6">
            Reviews & Testimonials
          </div>
          <h1 className="heading-display text-5xl md:text-7xl text-white max-w-3xl">
            What Patients <span className="italic text-gold-light">Say.</span>
          </h1>
          <div className="mt-8 flex items-center gap-6 text-white/80">
            <div className="flex gap-1">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  size={18}
                  className="fill-gold text-gold"
                  strokeWidth={0}
                />
              ))}
            </div>
            <div className="text-sm tracking-widest uppercase">4.9</div>
          </div>
        </div>
      </section>

      <VideoTestimonialsSection />

      <section className="py-24 md:py-32 container-x">
        <TestimonialsCarousel />
      </section>

      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={IMAGES.clinicInterior3}
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-ink/70" />
        </div>
        <div className="relative container-x text-center text-white">
          <h2 className="heading-display text-4xl md:text-6xl max-w-3xl mx-auto">
            Where Expertise, Artistry, and Genuine Care Come Together.
          </h2>
          <p className="mt-6 max-w-4xl mx-auto text-base md:text-lg text-white/90 leading-relaxed">
            Experience personalized dentistry built on advanced clinical
            excellence, artistic precision, and an unwavering commitment to your
            long-term oral health, function, and confidence.
          </p>
          <Link
            to="/contact"
            data-testid="reviews-cta"
            className="btn-outline-light mt-10 inline-flex"
          >
            Book a Consult.
          </Link>
        </div>
      </section>
    </div>
  );
};
